import { InlineBanner } from "components/ui/inline-banner";
import { InteractiveTextLink } from "components/ui/interactive-text-link";
import { PrimaryButton } from "components/ui/primary-button";
import React from "react";
import { PlanHealthBannerKind } from "./plan-health";

interface PlanHealthBannerProps {
    kind: PlanHealthBannerKind;
    onViewPlan: () => void;
    setupPlaybookMessage: string;
}

interface PlaybookLinkBannerProps {
    type: React.ComponentProps<typeof InlineBanner>["type"];
    message: string;
    linkLabel: string;
    onViewPlan: () => void;
}

const PlaybookLinkBanner: React.FC<PlaybookLinkBannerProps> = ({
    type,
    message,
    linkLabel,
    onViewPlan,
}) => (
    <InlineBanner type={type}>
        <div className="flex items-center justify-between gap-3">
            <p>{message}</p>
            <InteractiveTextLink
                className="shrink-0 text-brand-600 hover:text-brand-700"
                onClick={onViewPlan}>
                {linkLabel}
            </InteractiveTextLink>
        </div>
    </InlineBanner>
);

const noGoalsMessage = "No marketing goals have been set for you yet.";

export const PlanHealthBanner: React.FC<PlanHealthBannerProps> = ({
    kind,
    onViewPlan,
    setupPlaybookMessage,
}) => {
    switch (kind) {
        case "hidden":
            return null;
        case "setupPlaybook":
            return (
                <div className="rounded-lg bg-white p-6 text-center ring-1 ring-gray-300">
                    <p className="text-sm text-gray-600">{setupPlaybookMessage}</p>
                    <div className="mt-3 flex justify-center">
                        <PrimaryButton onClick={onViewPlan}>
                            Set up your playbook
                        </PrimaryButton>
                    </div>
                </div>
            );
        case "noGoalsWithoutPlaybook":
            return (
                <PlaybookLinkBanner
                    type="info"
                    message={noGoalsMessage}
                    linkLabel="Set up your playbook"
                    onViewPlan={onViewPlan}
                />
            );
        case "noGoals":
            return (
                <PlaybookLinkBanner
                    type="info"
                    message={noGoalsMessage}
                    linkLabel="View/edit playbook"
                    onViewPlan={onViewPlan}
                />
            );
        case "fallingShort":
            return (
                <PlaybookLinkBanner
                    type="warning"
                    message="Your playbook is not projected to meet your activity goals this month."
                    linkLabel="View/edit playbook"
                    onViewPlan={onViewPlan}
                />
            );
        case "onTrack":
            return (
                <PlaybookLinkBanner
                    type="success"
                    message="Your current playbook is projected to meet your goal this month."
                    linkLabel="View/edit playbook"
                    onViewPlan={onViewPlan}
                />
            );
        default: {
            const exhaustiveCheck: never = kind;
            throw new Error(
                `Unhandled plan health banner kind ${String(exhaustiveCheck)}`,
            );
        }
    }
};
